import React, { Fragment, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import SvgIcon from "../../Components/Common/Component/SvgIcon";
import CustomizerContext from "../../_helper/Customizer";
import { getStoredUser } from "../../api/auth";
import {
  canAccessMenuItem,
  filterVisibleMenuChildren,
  hasAnyVisibleMenuSection,
} from "../../constants/menuAccess";
import { getRemoteMenuLinkGrants } from "../../constants/rbacAccess";
import { isSuperAdmin } from "../../constants/credoraRoles";
import { getMenuIconIds, menuHref, menuPathMatches, resolveLayoutSlug, sectionMatchesPath } from "./Menu";
import SidebarChevron from "./SidebarChevron";
import { ChevronRight } from "react-feather";

const closeMobileSidebar = () => {
  if (window.innerWidth <= 991) {
    document.querySelector(".page-header")?.classList.add("close_icon");
    document.querySelector(".sidebar-wrapper")?.classList.add("close_icon");
  }
};

const SidebarMenuItems = ({ menuGroups, setMainMenu, sidebartoogle, setNavActive, activeClass }) => {
  const MENUITEMS = menuGroups || [];
  const user = getStoredUser();
  const superAdmin = isSuperAdmin(user);
  const { layout, layoutURL } = useContext(CustomizerContext);
  const layout1 = localStorage.getItem("sidebar_layout") || layout;
  const CurrentPath = window.location.pathname;
  const layoutSlug = resolveLayoutSlug(CurrentPath, layoutURL);

  const { t } = useTranslation();

  const menuGrantCount = getRemoteMenuLinkGrants().length;

  const visibleMenu = useMemo(() => {
    if (superAdmin) {
      return MENUITEMS;
    }
    return MENUITEMS.map((group) => ({
      ...group,
      Items: (group.Items || []).filter((section) => hasAnyVisibleMenuSection(user, section)),
    }));
  }, [MENUITEMS, user?.role, user?.id, superAdmin, menuGrantCount]);

  const toggletNavActive = (item) => {
    if (window.innerWidth <= 991) {
      document.querySelector(".page-header").className = "page-header close_icon";
      document.querySelector(".sidebar-wrapper").className = "sidebar-wrapper close_icon ";
      if (item.type === "sub") {
        document.querySelector(".page-header").className = "page-header";
        document.querySelector(".sidebar-wrapper").className = "sidebar-wrapper";
      }
    }
    if (!item.active) {
      MENUITEMS.map((a) => {
        a.Items.filter((Items) => {
          if (a.Items.includes(item)) Items.active = false;
          if (!Items.children) return false;
          Items.children.forEach((b) => {
            if (Items.children.includes(item)) {
              b.active = false;
            }
            if (!b.children) return false;
            b.children.forEach((c) => {
              if (b.children.includes(item)) {
                c.active = false;
              }
            });
          });
          return Items;
        });
        return a;
      });
    }
    item.active = !item.active;
    setMainMenu({ mainmenu: MENUITEMS });
  };

  const renderNestedLinks = (items, depth = 0) => {
    const visible = superAdmin ? items || [] : filterVisibleMenuChildren(user, items);
    if (!visible.length) {
      return null;
    }

    return visible.map((childrenItem, index) => {
      const childActive = menuPathMatches(CurrentPath, childrenItem.path);
      const nestedVisible = filterVisibleMenuChildren(user, childrenItem.children);

      if (childrenItem.type === "sub" && nestedVisible.length === 0) {
        return null;
      }

      return (
        <li key={`${childrenItem.title}-${index}-${depth}`}>
          {childrenItem.type === "sub" ? (
            <a
              href="javascript"
              className={`${childActive || sectionMatchesPath(childrenItem, CurrentPath) ? "active" : ""}`}
              onClick={(event) => {
                event.preventDefault();
                toggletNavActive(childrenItem);
              }}>
              {t(childrenItem.title)}
              <span className="sub-arrow">
                <ChevronRight size={12} strokeWidth={2} aria-hidden />
              </span>
              <SidebarChevron open={childrenItem.active || sectionMatchesPath(childrenItem, CurrentPath)} />
            </a>
          ) : null}

          {childrenItem.type === "link" ? (
            <Link
              to={menuHref(childrenItem.path, layoutSlug)}
              className={`${childActive ? "active" : ""}`}
              onClick={closeMobileSidebar}>
              {t(childrenItem.title)}
            </Link>
          ) : null}

          {nestedVisible.length > 0 ? (
            <ul
              className="nav-sub-childmenu submenu-content"
              style={
                childActive || childrenItem.active || sectionMatchesPath(childrenItem, CurrentPath)
                  ? { display: "block" }
                  : { display: "none" }
              }>
              {renderNestedLinks(childrenItem.children, depth + 1)}
            </ul>
          ) : null}
        </li>
      );
    });
  };

  return (
    <>
      {visibleMenu.map((Item, i) => (
        <Fragment key={i}>
          {Item.menutitle ? (
            <li className="sidebar-main-title">
              <div>
                <h6 className="lan-1">{t(Item.menutitle)}</h6>
              </div>
            </li>
          ) : null}
          {Item.Items.map((menuItem, sectionIndex) => {
            const visibleChildren = superAdmin
              ? menuItem.children || []
              : filterVisibleMenuChildren(user, menuItem.children);

            if (!superAdmin && menuItem.type === "sub" && visibleChildren.length === 0) {
              return null;
            }

            if (!superAdmin && menuItem.type === "link" && !canAccessMenuItem(user, menuItem)) {
              return null;
            }

            const sectionOpen = menuItem.active || sectionMatchesPath(menuItem, CurrentPath);

            return (
              <li
                className={`sidebar-list credora-nav-section ${sectionOpen ? "credora-nav-section--open" : ""}`}
                key={`${menuItem.title}-${sectionIndex}`}>
                {menuItem.type === "sub" ? (
                  <a
                    href="javascript"
                    className={`sidebar-link sidebar-title ${sectionOpen ? "active" : ""}`}
                    onClick={(event) => {
                      event.preventDefault();
                      setNavActive(menuItem);
                      activeClass();
                    }}>
                    <SvgIcon className="stroke-icon" iconId={getMenuIconIds(menuItem.icon).strokeId} />
                    <SvgIcon className="fill-icon" iconId={getMenuIconIds(menuItem.icon).fillId} />
                    <span>{t(menuItem.title)}</span>
                    {menuItem.badge ? <label className={menuItem.badge}>{menuItem.badgetxt}</label> : ""}
                    <SidebarChevron open={sectionOpen} />
                  </a>
                ) : null}

                {menuItem.type === "link" ? (
                  <Link
                    to={menuHref(menuItem.path, layoutSlug)}
                    className={`sidebar-link sidebar-title link-nav ${menuPathMatches(CurrentPath, menuItem.path) ? "active" : ""}`}
                    onClick={closeMobileSidebar}>
                    <SvgIcon className="stroke-icon" iconId={getMenuIconIds(menuItem.icon).strokeId} />
                    <SvgIcon className="fill-icon" iconId={getMenuIconIds(menuItem.icon).fillId} />
                    <span>{t(menuItem.title)}</span>
                    {menuItem.badge ? <label className={menuItem.badge}>{menuItem.badgetxt}</label> : ""}
                  </Link>
                ) : null}

                {visibleChildren.length > 0 ? (
                  <ul
                    className="sidebar-submenu credora-nav-submenu"
                    style={
                      layout1 !== "compact-sidebar compact-small"
                        ? sectionOpen
                          ? sidebartoogle
                            ? { opacity: 1, transition: "opacity 500ms ease-in" }
                            : { display: "block" }
                          : { display: "none" }
                        : { display: "none" }
                    }>
                    {renderNestedLinks(menuItem.children)}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </Fragment>
      ))}
    </>
  );
};

export default SidebarMenuItems;
